import { inject, Injector } from '@angular/core';
import {
	ModulesEnum,
	RentalAgreementRecordDto,
	RentalAgreementTypeEnum,
	TransactionRecordCategoryEnum,
	TransactionRecordDto,
	VehicleDamageRecordCategoryEnum,
	VehicleDamageRecordDto,
	VehicleInsuranceRecordCategoryEnum,
	VehicleInsuranceRecordDto,
	VehicleMaintenanceRecordCategoryEnum,
	VehicleMaintenanceRecordDto,
	VehicleRoadTaxRecordCategoryEnum,
	VehicleRoadTaxRecordDto,
	VehicleRoadWorthyCheckRecordCategoryEnum,
	VehicleRoadWorthyCheckRecordDto,
} from '@splitsecondconnect/ultramarine-shared';

import { FEATURES } from '../../../../core/features/features';
import { ListComponent } from '../../../../shared/components/list/list.component';
import { ModuleSelectorStrategy } from '../../../../shared/models/module-selector.strategy';
import { LIST_STRATEGY_TOKEN } from '../../../../shared/tokens/list-strategy.token';
import { getRentalAgreementFilters } from '../../rental-agreements/config/rental-agreement-filters.config';
import { RentalAgreementListStrategy } from '../../rental-agreements/config/rental-agreement-list-strategy';
import { RentalAgreementsRecordComponent } from '../../rental-agreements/rental-agreements-record/rental-agreements-record.component';
import { RentalAgreementsRecordResolver } from '../../rental-agreements/rental-agreements-record/rental-agreements-record.resolver';
import { getTransactionFilters } from '../../transactions/config/transaction-filters.config';
import { TransactionListStrategy } from '../../transactions/config/transaction-list-strategy';
import { TransactionsRecordComponent } from '../../transactions/transactions-record/transactions-record.component';
import { TransactionsRecordResolver } from '../../transactions/transactions-record/transactions-record.resolver';
import { getVehicleDamageFilters } from '../../vehicle-damages/config/vehicle-damage-filters.config';
import { VehicleDamageListStrategy } from '../../vehicle-damages/config/vehicle-damage-list-strategy';
import { VehicleDamagesRecordComponent } from '../../vehicle-damages/vehicle-damages-record/vehicle-damages-record.component';
import { VehicleDamagesRecordResolver } from '../../vehicle-damages/vehicle-damages-record/vehicle-damages-record.resolver';
import { getVehicleInsuranceFilters } from '../../vehicle-insurances/config/vehicle-insurance-filters.config';
import { VehicleInsuranceListStrategy } from '../../vehicle-insurances/config/vehicle-insurance-list-strategy';
import { VehicleInsurancesRecordComponent } from '../../vehicle-insurances/vehicle-insurances-record/vehicle-insurances-record.component';
import { VehicleInsurancesRecordResolver } from '../../vehicle-insurances/vehicle-insurances-record/vehicle-insurances-record.resolver';
import { getVehicleMaintenanceFilters } from '../../vehicle-maintenances/config/vehicle-maintenance-filters.config';
import { VehicleMaintenanceListStrategy } from '../../vehicle-maintenances/config/vehicle-maintenance-list-strategy';
import { VehicleMaintenancesRecordComponent } from '../../vehicle-maintenances/vehicle-maintenances-record/vehicle-maintenances-record.component';
import { VehicleMaintenancesRecordResolver } from '../../vehicle-maintenances/vehicle-maintenances-record/vehicle-maintenances-record.resolver';
import { getVehicleRoadTaxFilters } from '../../vehicle-road-taxes/config/vehicle-road-tax-filters.config';
import { VehicleRoadTaxListStrategy } from '../../vehicle-road-taxes/config/vehicle-road-tax-list-strategy';
import { VehicleRoadTaxesRecordComponent } from '../../vehicle-road-taxes/vehicle-road-taxes-record/vehicle-road-taxes-record.component';
import { VehicleRoadTaxesRecordResolver } from '../../vehicle-road-taxes/vehicle-road-taxes-record/vehicle-road-taxes-record.resolver';
import { getVehicleRoadWorthyCheckFilters } from '../../vehicle-road-worthy-checks/config/vehicle-road-worthy-check-filters.config';
import { VehicleRoadWorthyCheckListStrategy } from '../../vehicle-road-worthy-checks/config/vehicle-road-worthy-check-list-strategy';
import { VehicleRoadWorthyChecksRecordComponent } from '../../vehicle-road-worthy-checks/vehicle-road-worthy-checks-record/vehicle-road-worthy-checks-record.component';
import { VehicleRoadWorthyChecksRecordResolver } from '../../vehicle-road-worthy-checks/vehicle-road-worthy-checks-record/vehicle-road-worthy-checks-record.resolver';

export class VehicleModuleSelectorStrategy implements ModuleSelectorStrategy {
	#injector = inject(Injector);
	suggestTransaction = false;
	originModule = ModulesEnum.VEHICLES;
	options = [
		{
			key: 'rental-agreements|title',
			svgIcon: 'receipt_long',
			value: ModulesEnum.RENTAL_AGREEMENTS,
			component: ListComponent,
			featureFlag: FEATURES.rentalAgreements,
			partialDataKey: 'vehicleId',
			hiddenFilterKey: 'vehicles',
			class: 'col-span-2',
			filters: getRentalAgreementFilters().filter(
				(item) =>
					![ModulesEnum.VEHICLES, ModulesEnum.VEHICLE_CATEGORIES].includes(
						item.module,
					),
			),
			injector: Injector.create({
				providers: [
					{
						provide: LIST_STRATEGY_TOKEN,
						useClass: RentalAgreementListStrategy,
					},
				],
				parent: this.#injector,
			}),
			newRecordComponent: RentalAgreementsRecordComponent,
			newRecordResolver: RentalAgreementsRecordResolver,
			newRecordPartialData: (id: number): Partial<RentalAgreementRecordDto> => {
				return {
					type: RentalAgreementTypeEnum.WALK_IN,
					vehicleId: id,
				};
			},
		},
		{
			key: 'maintenances|title',
			svgIcon: 'car_repair',
			value: ModulesEnum.VEHICLE_MAINTENANCES,
			component: ListComponent,
			featureFlag: FEATURES.vehicleMaintenances,
			partialDataKey: 'vehicleId',
			hiddenFilterKey: 'vehicles',
			filters: getVehicleMaintenanceFilters().filter(
				(item) =>
					![ModulesEnum.VEHICLES, ModulesEnum.VEHICLE_CATEGORIES].includes(
						item.module,
					),
			),
			injector: Injector.create({
				providers: [
					{
						provide: LIST_STRATEGY_TOKEN,
						useClass: VehicleMaintenanceListStrategy,
					},
				],
				parent: this.#injector,
			}),
			newRecordComponent: VehicleMaintenancesRecordComponent,
			newRecordResolver: VehicleMaintenancesRecordResolver,
			newRecordPartialData: (
				id: number,
			): Partial<VehicleMaintenanceRecordDto> => {
				return {
					recordCategory: VehicleMaintenanceRecordCategoryEnum.VEHICLE,
					vehicleId: id,
				};
			},
		},
		{
			key: 'road-worthy-checks|title',
			svgIcon: 'inventory',
			value: ModulesEnum.VEHICLE_ROAD_WORTHY_CHECKS,
			component: ListComponent,
			featureFlag: FEATURES.vehicleRoadWorthyChecks,
			partialDataKey: 'vehicleId',
			hiddenFilterKey: 'vehicles',
			filters: getVehicleRoadWorthyCheckFilters().filter(
				(item) =>
					![ModulesEnum.VEHICLES, ModulesEnum.VEHICLE_CATEGORIES].includes(
						item.module,
					),
			),
			injector: Injector.create({
				providers: [
					{
						provide: LIST_STRATEGY_TOKEN,
						useClass: VehicleRoadWorthyCheckListStrategy,
					},
				],
				parent: this.#injector,
			}),
			newRecordComponent: VehicleRoadWorthyChecksRecordComponent,
			newRecordResolver: VehicleRoadWorthyChecksRecordResolver,
			newRecordPartialData: (
				id: number,
			): Partial<VehicleRoadWorthyCheckRecordDto> => {
				return {
					recordCategory: VehicleRoadWorthyCheckRecordCategoryEnum.VEHICLE,
					vehicleId: id,
				};
			},
		},
		{
			key: 'road-taxes|title',
			svgIcon: 'account_balance',
			value: ModulesEnum.VEHICLE_ROAD_TAXES,
			component: ListComponent,
			featureFlag: FEATURES.vehicleRoadTaxes,
			partialDataKey: 'vehicleId',
			hiddenFilterKey: 'vehicles',
			filters: getVehicleRoadTaxFilters().filter(
				(item) =>
					![ModulesEnum.VEHICLES, ModulesEnum.VEHICLE_CATEGORIES].includes(
						item.module,
					),
			),
			injector: Injector.create({
				providers: [
					{
						provide: LIST_STRATEGY_TOKEN,
						useClass: VehicleRoadTaxListStrategy,
					},
				],
				parent: this.#injector,
			}),
			newRecordComponent: VehicleRoadTaxesRecordComponent,
			newRecordResolver: VehicleRoadTaxesRecordResolver,
			newRecordPartialData: (id: number): Partial<VehicleRoadTaxRecordDto> => {
				return {
					recordCategory: VehicleRoadTaxRecordCategoryEnum.VEHICLE,
					vehicleId: id,
				};
			},
		},
		{
			key: 'vehicle-insurances|title',
			svgIcon: 'gavel',
			value: ModulesEnum.VEHICLE_INSURANCES,
			component: ListComponent,
			featureFlag: FEATURES.vehicleInsurances,
			partialDataKey: 'vehicleId',
			hiddenFilterKey: 'vehicles',
			filters: getVehicleInsuranceFilters().filter(
				(item) =>
					![ModulesEnum.VEHICLES, ModulesEnum.VEHICLE_CATEGORIES].includes(
						item.module,
					),
			),
			injector: Injector.create({
				providers: [
					{
						provide: LIST_STRATEGY_TOKEN,
						useClass: VehicleInsuranceListStrategy,
					},
				],
				parent: this.#injector,
			}),
			newRecordComponent: VehicleInsurancesRecordComponent,
			newRecordResolver: VehicleInsurancesRecordResolver,
			newRecordPartialData: (
				id: number,
			): Partial<VehicleInsuranceRecordDto> => {
				return {
					recordCategory: VehicleInsuranceRecordCategoryEnum.VEHICLE,
					vehicleId: id,
				};
			},
		},
		{
			key: 'transactions|title',
			svgIcon: 'payment',
			value: ModulesEnum.TRANSACTIONS,
			component: ListComponent,
			featureFlag: FEATURES.transactions,
			partialDataKey: 'vehicleId',
			hiddenFilterKey: 'vehicles',
			filters: getTransactionFilters().filter(
				(item) =>
					![ModulesEnum.VEHICLES, ModulesEnum.VEHICLE_CATEGORIES].includes(
						item.module,
					),
			),
			injector: Injector.create({
				providers: [
					{ provide: LIST_STRATEGY_TOKEN, useClass: TransactionListStrategy },
				],
				parent: this.#injector,
			}),
			newRecordComponent: TransactionsRecordComponent,
			newRecordResolver: TransactionsRecordResolver,
			newRecordPartialData: (id: number): Partial<TransactionRecordDto> => {
				return {
					recordCategory: TransactionRecordCategoryEnum.VEHICLE,
					vehicleId: id,
				};
			},
		},
		{
			key: 'damages|title',
			svgIcon: 'bolt',
			value: ModulesEnum.VEHICLE_DAMAGES,
			component: ListComponent,
			featureFlag: FEATURES.vehicleDamages,
			partialDataKey: 'vehicleId',
			hiddenFilterKey: 'vehicles',
			filters: getVehicleDamageFilters().filter(
				(item) =>
					![ModulesEnum.VEHICLES, ModulesEnum.VEHICLE_CATEGORIES].includes(
						item.module,
					),
			),
			injector: Injector.create({
				providers: [
					{ provide: LIST_STRATEGY_TOKEN, useClass: VehicleDamageListStrategy },
				],
				parent: this.#injector,
			}),
			class: 'md:col-span-2',
			newRecordComponent: VehicleDamagesRecordComponent,
			newRecordResolver: VehicleDamagesRecordResolver,
			newRecordPartialData: (id: number): Partial<VehicleDamageRecordDto> => {
				return {
					recordCategory: VehicleDamageRecordCategoryEnum.VEHICLE,
					vehicleId: id,
				};
			},
		},
	];
}
